import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiFetch, unwrapData } from '../utils/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Plus, Building2, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { captureLog } from '@/lib/errorReporter';
import { usePermissions } from '@/components/hooks/usePermissions';
import { PERMS } from '@/constants/permissions';
const __LOG_CTX = { context: 'src/components/CompanySwitcher' };

export default function CompanySwitcher({ open, onOpenChange, currentCompany, onCompanyChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canAddCompany = hasPermission(PERMS.ROLES_MANAGE);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  // #817 — guard de concurrencia. `switchingRef` es un mutex SÍNCRONO: dos clics
  // en <5ms entran al handler antes de que React re-renderice, así que un estado
  // async (`disabled`) no basta para descartar el segundo. El ref se setea de
  // forma síncrona y bloquea cualquier POST en cascada. `switchingId` es el
  // espejo en estado para el feedback visual (disabled + spinner).
  const switchingRef = useRef(false);
  const [switchingId, setSwitchingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    // #817 — al re-abrir el dialog, limpiar cualquier guard residual.
    switchingRef.current = false;
    setSwitchingId(null);

    async function loadCompanies() {
      try {
        setLoading(true);
        // Endpoint dedicado: lista cross-tenant las companies del user
        // autenticado. Devuelve {id, name, cif, logo_url, role, is_default,
        // user_company_id} por cada membresía.
        const response = await apiFetch('/api/auth/companies');
        setCompanies(unwrapData(response));
      } catch (error) {
        captureLog('error', __LOG_CTX, 'Error loading companies:', error);
        toast.error(t('nav.error_loading_companies'));
      } finally {
        setLoading(false);
      }
    }
    
    loadCompanies();
  }, [open]);

  const switchCompany = async (company) => {
    // #817 — mutex síncrono: descarta clics en cascada (doble-clic rápido en
    // dos empresas) antes de que React re-renderice los botones `disabled`.
    // Sin esto se disparaban N POST /switch-company concurrentes, con riesgo de
    // estado de sesión inconsistente (el effectiveRole se sobreescribía).
    if (switchingRef.current) return;
    switchingRef.current = true;
    setSwitchingId(company.id);
    // FASE 4 Sub-PR 4.3 — una sola call al endpoint del BE.
    // Antes: 2×N requests `PUT /api/user-companies/:id { is_default }`.
    // Ahora: BE hace toda la operación atomic en una tx + purga Redis +
    // invalida cache de permisos. Performance + UX consistente.
    //
    // D7-04 — este componente ya NO postea a `/api/auth/switch-company` por su
    // cuenta. Delega en `onCompanyChange`, que acaba en el `switchCompany` del
    // contexto (`useApp`) con `hardReset: true`. Dos razones:
    //
    //   1. La copia local hacía `invalidateQueries()`, que marca stale pero
    //      SIRVE el dato cacheado mientras refetchea → un frame con cifras de
    //      la empresa anterior. El contexto con `hardReset` hace `clear()`.
    //   2. Había DOS cambios de empresa por clic: este POST y, acto seguido,
    //      el `switchCompany(company.id)` de `Layout.handleCompanyChange`
    //      disparado por este mismo `onCompanyChange`. Un clic, un cambio.
    //
    // El mutex de #817 se queda: sigue habiendo una operación async por clic.
    try {
      await onCompanyChange(company);
      toast.success(t('nav.company_switched', { name: company.name }));
      onOpenChange(false);
    } catch (error) {
      captureLog('error', __LOG_CTX, 'Error switching company:', error);
      toast.error(t('nav.error_switching_company'));
    } finally {
      // Reset siempre: en éxito el dialog se cierra (no estorba); en error
      // permite reintentar.
      switchingRef.current = false;
      setSwitchingId(null);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cambiar empresa</DialogTitle>
        </DialogHeader>

        <div className="space-y-2 py-4">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              Cargando...
            </div>
          ) : companies.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No hay empresas disponibles
            </div>
          ) : (
            companies.map((company) => (
              <button
                key={company.id}
                onClick={() => switchCompany(company)}
                disabled={switchingId !== null}
                className={cn(
                  "w-full flex items-center justify-between p-3 rounded-lg border transition-colors",
                  "disabled:opacity-60 disabled:cursor-not-allowed",
                  currentCompany?.id === company.id
                    ? "bg-blue-50 border-blue-200"
                    : "hover:bg-muted border-border"
                )}
              >
                <div className="flex items-center gap-3">
                  {company.logo_url ? (
                    <img
                      src={company.logo_url}
                      alt={company.name}
                      className="w-10 h-10 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                  )}
                  <div className="text-left">
                    <div className="font-medium text-foreground">{company.name}</div>
                    <div className="text-sm text-muted-foreground capitalize">{company.role}</div>
                  </div>
                </div>
                {switchingId === company.id ? (
                  <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                ) : currentCompany?.id === company.id ? (
                  <Check className="w-5 h-5 text-blue-600" />
                ) : null}
              </button>
            ))
          )}
        </div>

        {canAddCompany && (
          <Button
            variant="outline"
            className="w-full"
            onClick={() => {
              onOpenChange(false);
              // Al embudo, no a un diálogo de nombre + CIF: ese atajo creaba
              // sociedades sin forma jurídica ni territorio fiscal, que es lo
              // que decide IVA / IGIC / IPSI. Una empresa se da de alta igual
              // sea la primera o la quinta.
              navigate('/bienvenida?nueva=1');
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Añadir empresa
          </Button>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}